import { useState, useEffect, useCallback } from 'react';
import * as bookmarkService from '../services/bookmarkService';
import { getStoredReaderToken } from '../services/apiClient';

const BOOKMARKS_STORAGE_KEY = 'bloomwik_bookmarks';
const LEGACY_BOOKMARKS_KEY = 'blogify_bookmarks';

function readLocalBookmarksRaw() {
  return (
    localStorage.getItem(BOOKMARKS_STORAGE_KEY) ||
    localStorage.getItem(LEGACY_BOOKMARKS_KEY)
  );
}

export const useBookmark = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookmarkStatus, setBookmarkStatus] = useState({});

  const loadFromLocalStorage = useCallback(() => {
    try {
      const stored = readLocalBookmarksRaw();
      if (stored) {
        const localBookmarks = JSON.parse(stored);
        setBookmarks(localBookmarks);
        const statusMap = {};
        localBookmarks.forEach((bookmark) => {
          if (bookmark._id || bookmark.id) {
            statusMap[bookmark._id || bookmark.id] = true;
          }
        });
        setBookmarkStatus(statusMap);
      }
    } catch (error) {
      console.error('Error loading bookmarks from localStorage:', error);
    }
  }, []);

  const loadBookmarks = useCallback(async () => {
    const token = getStoredReaderToken();
    if (!token) {
      loadFromLocalStorage();
      return;
    }

    setLoading(true);
    try {
      const response = await bookmarkService.getBookmarks({ limit: 100 });
      if (response.success) {
        setBookmarks(response.data || []);
        const statusMap = {};
        response.data.forEach((bookmark) => {
          if (bookmark.article && bookmark.article._id) {
            statusMap[bookmark.article._id] = true;
          }
        });
        setBookmarkStatus(statusMap);
      }
    } catch {
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  }, [loadFromLocalStorage]);

  useEffect(() => {
    loadBookmarks();
  }, [loadBookmarks]);

  const addBookmark = async (article) => {
    const articleId = article._id || article.id;
    if (!articleId) {
      console.error('Article ID is required');
      return false;
    }

    try {
      const response = await bookmarkService.addBookmark(articleId);
      if (response.success) {
        setBookmarks((prev) => [...prev, response.data]);
        setBookmarkStatus((prev) => ({ ...prev, [articleId]: true }));
        return true;
      }
    } catch {
      try {
        const newBookmarks = [...bookmarks, article];
        setBookmarks(newBookmarks);
        localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(newBookmarks));
        setBookmarkStatus((prev) => ({ ...prev, [articleId]: true }));
        return true;
      } catch (localError) {
        console.error('Error saving bookmark to localStorage:', localError);
        return false;
      }
    }
    return false;
  };

  const removeBookmark = async (articleIdOrSlug) => {
    let articleId = articleIdOrSlug;
    const bookmark = bookmarks.find(
      (b) =>
        (b.article && (b.article._id === articleIdOrSlug || b.article.id === articleIdOrSlug)) ||
        b.slug === articleIdOrSlug ||
        b._id === articleIdOrSlug
    );

    if (bookmark && bookmark.article) {
      articleId = bookmark.article._id || bookmark.article.id;
    } else if (bookmark && bookmark._id) {
      articleId = bookmark._id;
    }

    try {
      const response = await bookmarkService.removeBookmark(articleId);
      if (response.success) {
        setBookmarks((prev) =>
          prev.filter((b) => {
            const bArticleId = b.article?._id || b.article?.id || b._id || b.id;
            return bArticleId !== articleId;
          })
        );
        setBookmarkStatus((prev) => {
          const newStatus = { ...prev };
          delete newStatus[articleId];
          return newStatus;
        });
        return true;
      }
    } catch {
      try {
        const newBookmarks = bookmarks.filter((b) => {
          const bArticleId = b.article?._id || b.article?.id || b._id || b.id;
          return bArticleId !== articleId;
        });
        setBookmarks(newBookmarks);
        localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(newBookmarks));
        setBookmarkStatus((prev) => {
          const newStatus = { ...prev };
          delete newStatus[articleId];
          return newStatus;
        });
        return true;
      } catch (localError) {
        console.error('Error removing bookmark from localStorage:', localError);
        return false;
      }
    }
    return false;
  };

  const isBookmarked = (articleIdOrSlug) => {
    if (bookmarkStatus[articleIdOrSlug]) {
      return true;
    }

    return bookmarks.some((bookmark) => {
      const bArticleId = bookmark.article?._id || bookmark.article?.id || bookmark._id || bookmark.id;
      const bSlug = bookmark.article?.slug || bookmark.slug;
      return bArticleId === articleIdOrSlug || bSlug === articleIdOrSlug;
    });
  };

  const toggleBookmark = async (article) => {
    const articleId = article._id || article.id;
    const slug = article.slug;

    if (isBookmarked(articleId || slug)) {
      return removeBookmark(articleId || slug);
    }
    return addBookmark(article);
  };

  const checkBookmarkStatus = async (articleId) => {
    try {
      const response = await bookmarkService.checkBookmark(articleId);
      if (response.success) {
        setBookmarkStatus((prev) => ({
          ...prev,
          [articleId]: response.isBookmarked,
        }));
        return response.isBookmarked;
      }
    } catch {
      return isBookmarked(articleId);
    }
    return false;
  };

  return {
    bookmarks,
    loading,
    addBookmark,
    removeBookmark,
    isBookmarked,
    toggleBookmark,
    checkBookmarkStatus,
    refreshBookmarks: loadBookmarks,
  };
};
